import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { identity } from "lodash";
import { PopupClickContainer } from "@similarweb/ui-components";
import { ChipWrapper } from "../styledComponents";
import { SimpleChipItemWrap } from "@similarweb/ui-components";
import { AdvancedFilterButton } from "pro-features/components/filtersPanel/src/filtersPanel";
import type {
    IAutocompleteKeyword,
    IAutocompleteKeywordGroup,
} from "pro-features/components/AutocompleteKeywords/types/AutocompleteKeywordGroupTypes";
import { MultiIncludeExcludePopupContent } from "./MultiIncludeExcludePopupContent";
import { GlobalStyles } from "./styledComponents";
import type { IRule, IRuleItem, MultiIncludeExcludeKeywordsProps } from "./types";
import { createNewRow, MultiIncludeExcludeKeywordGroupsTextField } from "./utils";
import { allTrackers } from "app/services/track/track";

export const MultiIncludeExcludeKeywords: FC<MultiIncludeExcludeKeywordsProps> = ({
    rules: initialRules,
    onMultiIncludeExcludeChange,
    getButtonText,
    getTextField = MultiIncludeExcludeKeywordGroupsTextField,
    maxRules = 3,
    maxKeywordsPerRule = 20,
    withMatchType = true,
    getRuleSeparatorText,
}) => {
    const filterPopupRef = useRef(null);
    const contentContainerRef = useRef(null);
    const didApply = useRef(false);
    const [rules, setRules] = useState<IRule[]>(initialRules ?? [createNewRow()]);

    useEffect(() => {
        if (!rules.length) {
            setRules([createNewRow()]);
        }
    }, [rules]);

    const onChipdownToggle = (isOpen: boolean) => {
        if (isOpen) {
            didApply.current = false;
            allTrackers.dispatchTrackEvent("Drop Down", "click", "Open keyword filter");
        } else if (!didApply.current) {
            onApply(false);
        }
    };

    const onCancelClick = (e: Event) => {
        e.stopPropagation();
        setRules(initialRules ?? [createNewRow()]);
        filterPopupRef.current.closePopup();
    };

    const addRule = () => {
        allTrackers.dispatchTrackEvent("Drop Down", "click", "Add rule");
        setRules([...rules, createNewRow()]);
        queueMicrotask(() => {
            contentContainerRef.current.scrollTop = contentContainerRef.current.scrollHeight;
        });
    };

    const deleteRule = (index: number) => {
        setRules([...rules.slice(0, index), ...rules.slice(index + 1)]);
    };

    const onRowTypeChange = (index: number, rule: Partial<Pick<IRule, "matchType" | "type">>) => {
        setRules([...rules.slice(0, index), createNewRow({ ...rules[index], ...rule }), ...rules.slice(index + 1)]);
    };

    const onKeywordSelected = (item: string | IAutocompleteKeyword | IAutocompleteKeywordGroup, index: number) => {
        const trackKeywordAdd = (keyword: string, isGroup = false) => {
            allTrackers.dispatchTrackEvent(
                "Drop Down",
                "click",
                `keyword added/${keyword || ""}/${isGroup ? "group" : "keyword"}`,
            );
        };
        const handleKeyword = (keyword: string) => {
            const commaSeparatedItems = keyword
                .split(",")
                .map((item) => item.trim())
                .filter(identity);

            trackKeywordAdd(commaSeparatedItems.join(","));

            const removeDuplicates = (items: IRuleItem[]) => {
                return items.filter(
                    (item, index, self) =>
                        index === self.findIndex((t) => t.type === item.type && t.value === item.value),
                );
            };

            if (commaSeparatedItems.length > 1) {
                setRules((currentRules) => {
                    currentRules[index].items.push(
                        ...commaSeparatedItems.map((value) => ({ type: "keyword", value }) as const),
                    );
                    currentRules[index].items = removeDuplicates(currentRules[index].items);
                    currentRules[index].items = currentRules[index].items.slice(0, maxKeywordsPerRule);
                    return [...currentRules];
                });
            } else if (keyword.trim().length) {
                setRules((currentRules) => {
                    currentRules[index].items.push({ type: "keyword", value: keyword });
                    currentRules[index].items = removeDuplicates(currentRules[index].items);
                    return [...currentRules];
                });
            }
        };

        if (typeof item === "string") {
            handleKeyword(item);
        } else if (item.hasOwnProperty("name")) {
            handleKeyword((item as IAutocompleteKeyword).name);
        } else {
            trackKeywordAdd((item as IAutocompleteKeywordGroup).Name, true);
            setRules((currentRules) => {
                currentRules[index].items.push({
                    type: "keywordList",
                    value: (item as IAutocompleteKeywordGroup).Id,
                });
                return [...currentRules];
            });
        }
    };

    const onRemoveKeyword = (keyword: string, type: "keyword" | "keywordList", _, index: number) => () => {
        setRules((currentRules) => {
            currentRules[index].items = currentRules[index].items.filter(
                (item) => item.type === type && item.value !== keyword,
            );
            return [...currentRules];
        });
    };

    const onApply = (closePopup = true) => {
        didApply.current = true;
        // remove empty rules
        const rulesToSubmit = rules.filter((rule) => rule.items.length);
        onMultiIncludeExcludeChange(rulesToSubmit);
        if (closePopup) {
            filterPopupRef.current.closePopup();
        }

        /**
         * Track event
         * @returns {{keywordsCount: number, listCount: number, ruleCount: number, ruleTypes: string}}} - where ruleTypes is a comma separated string of rule types+match types
         */
        const { keywordCount, listCount, ruleCount, ruleTypes } = rulesToSubmit.reduce(
            (acc, rule) => {
                acc.ruleCount++;
                acc.ruleTypes += `, ${rule.type}+${rule.matchType}`;
                acc.keywordCount += rule.items.filter((item) => item.type === "keyword").length;
                acc.listCount += rule.items.filter((item) => item.type === "keywordList").length;
                return acc;
            },
            { keywordCount: 0, listCount: 0, ruleCount: 0, ruleTypes: "" },
        );

        allTrackers.dispatchTrackEvent(
            "Drop Down",
            "click",
            `keyword filter applied/${keywordCount.toString() || ""}And${listCount.toString() || ""}/${ruleCount.toString() || ""} rules${ruleTypes || ""}`,
        );
    };

    const onResetClick = (e) => {
        e.stopPropagation();
        onMultiIncludeExcludeChange([]);
    };

    const popupConfig = {
        width: 750,
        enabled: true,
        placement: "ontop-left",
        cssClassContent: "multiIncludeExclude--Popup-container",
        cssClassContainer: "Popup-element-wrapper-triangle multiIncludeExclude--Popup-container",
        onToggle: onChipdownToggle,
    };

    return (
        <>
            <GlobalStyles />
            <PopupClickContainer
                data-automation="keyword-multi-include-exclude-keywords-chipdown"
                config={popupConfig}
                ref={filterPopupRef}
                content={() => (
                    <MultiIncludeExcludePopupContent
                        fieldType="keywords"
                        rules={rules}
                        maxItemsPerRule={maxKeywordsPerRule}
                        addRow={addRule}
                        deleteRow={deleteRule}
                        onRowTypeChange={onRowTypeChange}
                        onItemSelected={onKeywordSelected}
                        onRemoveItem={onRemoveKeyword}
                        onApply={onApply}
                        onCancelClick={onCancelClick}
                        maxRules={maxRules}
                        withMatchType={withMatchType}
                        setContainerRef={(ref) => (contentContainerRef.current = ref)}
                        getRuleSeparatorText={getRuleSeparatorText}
                    >
                        {getTextField}
                    </MultiIncludeExcludePopupContent>
                )}
            >
                <div>
                    {rules[0]?.items?.length ? (
                        <ChipWrapper>
                            <SimpleChipItemWrap
                                text={getButtonText(rules)}
                                onCloseItem={onResetClick}
                            />
                        </ChipWrapper>
                    ) : (
                        <AdvancedFilterButton>{getButtonText()}</AdvancedFilterButton>
                    )}
                </div>
            </PopupClickContainer>
        </>
    );
};
